import {
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CommonStyle } from "Components";
import { RouteUrls } from "Constant/RouteUrls";
import { UserInitialStateType, ReduxScreenNavType } from "DataTypes/Redux";
import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLeftHead, setNavArr, setRightHead } from "Redux/Screen/Action";
import { store } from "Redux/Store";
import CommonFormatter from "Utils/Common.Formatter";
import CarePlanApi from "Service/CarePlan.api";
import UserApi from "Service/User.api";
import UsersFormatter from "Utils/Users.Formatter";
import { CheckArr, CheckNumber, CheckStringEmpty } from "Utils/common";

type ProviderAssignedState = {
  oldProviderId?: number | null;
  notificationId?: number | null;
};

type AssignedPatientRow = {
  patientId: string;
  patientName: string;
  careplanId: number | null;
  programAbbreviation: string;
  careplanState: string;
  rolesToReplace: string[];
  reassignedBy: string;
  reassignedAt: string;
};

const ProviderAssignedWorklist: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = (location.state || {}) as ProviderAssignedState;
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const [rows, setRows] = useState<AssignedPatientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [oldProviderLabel, setOldProviderLabel] = useState("");
  const oldProviderId = routeState.oldProviderId || null;

  useEffect(() => {
    dispatch(setLeftHead(""));
    dispatch(setRightHead(""));
    const { navArr } = store.getState().screen;
    const withoutPatientNav = navArr.filter(
      (el: ReduxScreenNavType) => el.navType !== "patient"
    );
    const hasOrgNav = withoutPatientNav.some(
      (el: ReduxScreenNavType) => el.navType === "org"
    );
    const navType: ReduxScreenNavType["navType"] = hasOrgNav ? "org" : "my-items";
    const siblingNav = withoutPatientNav.filter(
      (el: ReduxScreenNavType) => el.navType === navType
    );
    const level =
      siblingNav.length > 0
        ? Math.max(...siblingNav.map((el: ReduxScreenNavType) => el.level)) + 1
        : 1;
    dispatch(setNavArr(withoutPatientNav));
    CommonFormatter.HandleNavArr({
      id: "provider-assigned",
      label: "Patients Assigned to You",
      path: `/${RouteUrls.hs}/${RouteUrls.providerAssigned}`,
      level,
      link: RouteUrls.providerAssigned,
      navType,
    });
  }, [dispatch]);

  useEffect(() => {
    if (oldProviderId) {
      loadData(oldProviderId);
    }
  }, [oldProviderId]);

  const loadData: (providerId: number) => Promise<void> = async (providerId) => {
    setLoading(true);
    const userRes = await UserApi.read({ id1: providerId });
    if (userRes?.success) {
      const user = UsersFormatter.VerifyReadResponse(userRes.data);
      setOldProviderLabel(user.fullNameDes || "Provider");
    }
    const res = await CarePlanApi.ProviderAssignmentAssignedCareplans({
      id1: providerId,
    });
    if (res?.success) {
      setRows(
        CheckArr(res.data).map((row: any) => ({
          patientId: CheckStringEmpty(row?.patientId),
          patientName: CheckStringEmpty(row?.patientName),
          careplanId: CheckNumber(row?.careplanId) || null,
          programAbbreviation: CheckStringEmpty(row?.programAbbreviation),
          careplanState: CheckStringEmpty(row?.careplanState),
          rolesToReplace: CheckArr(row?.rolesToReplace),
          reassignedBy:
            CheckStringEmpty(row?.reassignedBy) ||
            CheckStringEmpty(row?.updatedByFullname),
          reassignedAt:
            CheckStringEmpty(row?.reassignedAt) ||
            CheckStringEmpty(row?.updatedAt),
        }))
      );
    } else {
      setRows([]);
    }
    setLoading(false);
  };

  const handlePatientClick: (patientId: string) => void = (patientId) => {
    if (!patientId) return;
    navigate(
      `/${RouteUrls.hs}/${RouteUrls.patient}/${patientId}/${RouteUrls.clinicalSummary}`
    );
  };

  const formatRoles = (roles: string[]) => {
    if (!roles.length) return "-";
    return roles
      .map((role) => {
        if (role === "RENDERING") return "Rendering Provider";
        if (role === "SUPERVISING") return "Supervising Provider";
        if (role === "BILLABLE") return "Care Plan Rendering Provider";
        return role;
      })
      .join(", ");
  };

  return (
    <Box height="100%" sx={{ overflow: "auto", py: 2 }}>
      <Box sx={{ ...CommonStyle.sxRow, justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h6">Patients Assigned to You</Typography>
          <Typography variant="body2">
            Patients assigned to you following a provider transition.
          </Typography>
          {oldProviderLabel && (
            <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
              Assigned from: {oldProviderLabel}
            </Typography>
          )}
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Total patients: {rows.length}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ ...CommonStyle.sxRowCenter, height: 180 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <TableContainer
          sx={{
            maxHeight: "calc(100vh - 280px)",
            minHeight: 220,
            overflow: "auto",
            ...CommonStyle.sxWhiteBox,
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Patient</TableCell>
                <TableCell>Your Role(s)</TableCell>
                <TableCell>Careplan Signed Off</TableCell>
                <TableCell>Program</TableCell>
                <TableCell>Assigned By</TableCell>
                <TableCell>Assigned At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={`${row.careplanId}-${row.patientId}`}>
                  <TableCell>
                    <Button
                      variant="text"
                      disabled={!row.patientId}
                      onClick={() => handlePatientClick(row.patientId)}
                      sx={{
                        minWidth: 0,
                        p: 0,
                        color: "#002b5c",
                        fontWeight: 600,
                        textDecoration: "underline",
                        textTransform: "none",
                        justifyContent: "flex-start",
                      }}
                    >
                      {row.patientName || "-"}
                    </Button>
                  </TableCell>
                  <TableCell>{formatRoles(row.rolesToReplace)}</TableCell>
                  <TableCell>
                    <SignedOffChip state={row.careplanState} />
                  </TableCell>
                  <TableCell>{row.programAbbreviation || "-"}</TableCell>
                  <TableCell>{row.reassignedBy || "-"}</TableCell>
                  <TableCell>{row.reassignedAt || "-"}</TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    No assigned patients found for this transition.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

const SignedOffChip: FC<{ state: string }> = ({ state }) => {
  const signedOff =
    CheckStringEmpty(state).toLowerCase() === "pending for recurring";
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{
        px: 1,
        py: 0.25,
        borderRadius: 1,
        color: signedOff ? "#008a3d" : "#d35400",
        backgroundColor: signedOff ? "#d9f4e4" : "#fff0df",
        fontWeight: 600,
      }}
    >
      {state ? (signedOff ? "Yes" : "No") : "-"}
    </Typography>
  );
};

export default ProviderAssignedWorklist;
